// Геометрия колонок оболочки — ЕДИНСТВЕННОЕ место, где решается фактическая
// ширина боковых панелей на desktop.
//
// Задача (жалоба 2026-08-15): на ноутбуке 1280–1350px курс открывался с уже
// свёрнутой панелью плана. Порог 1351px нигде не был записан — он вытекал из
// арифметики «окно − 400 (репетитор) − 64 (рельс) − 328 (панель) < 560 (центр)»:
// как только предпочтения переставали помещаться ЦЕЛИКОМ, колонка схлопывалась
// целиком. Теперь окно сначала отбирает у колонок пиксели (до их минимумов) и
// только потом — саму колонку; на desktop до самого 1024px обе панели остаются
// на экране, а ниже 1024 включается мобильная композиция со шторками.
//
// Почему считаем в JS, а не флексом: колонки лежат в разных поддеревьях —
// панель курса внутри `WorkspaceShell`, панель репетитора `position: fixed` у
// правого края, центр отжимается `margin-right` у `<main>`. Общего flex-родителя
// у них нет, договориться о ширинах браузеру не через что.
//
// Известный компромисс: пока окно в «сжатой» полосе (1024…FULL), ручка ресайза
// панели идёт мягче курсора — тянем предпочтение, а рисуется сжатая доля от
// него. Движение монотонное и упирается в минимум, скачков нет.
import { useRouterState } from "@tanstack/react-router";
import { useMemo } from "react";
import { type ShrinkTrack, shrinkToFit } from "@/lib/panel-shrink";
import { useIsLgUp, useViewportWidth } from "@/lib/platform-detect";
import {
	COURSE_APP_RAIL_WIDTH,
	COURSE_CENTER_COMPRESSED_MIN_WIDTH,
	COURSE_CENTER_MIN_WIDTH,
	COURSE_LEFT_MIN_WIDTH,
	useCourseWorkspaceStore,
} from "@/stores/course-workspace";
import { useSidePanelStore } from "@/stores/side-panel";
import { SIDE_PANEL_MIN_WIDTH, useSidePanelWidthStore } from "@/stores/side-panel-width";
import { isWorkspaceRoute } from "./_layout-state";

/**
 * Комфортная ширина контента обычной страницы (не воркспейс). То же число, по
 * которому оболочка сворачивает рельс приложения под широкую панель.
 */
export const REGULAR_CONTENT_COMFORT_WIDTH = 680;
/** Ниже этого контент обычной страницы не жмём — дальше сжимается панель. */
export const REGULAR_CONTENT_MIN_WIDTH = 600;
/** Ширина свёрнутого рельса приложения (в воркспейсе он свёрнут всегда). */
const COLLAPSED_RAIL_WIDTH = COURSE_APP_RAIL_WIDTH;

/** Колонка, которой сейчас нет на экране: место целиком достаётся соседям. */
const ABSENT: ShrinkTrack = { pref: 0, min: 0 };

export interface ShellGeometryInput {
	viewportWidth: number;
	/** ≥ `lg`: ниже боковые колонки не рендерятся вовсе (шторки). */
	isDesktop: boolean;
	/** Воркспейс курса — три колонки вместо двух. */
	isWorkspace: boolean;
	/** Открыта ли правая панель оболочки (репетитор или уведомления). */
	panelOpen: boolean;
	/** Настройка пользователя: ширина правой панели. */
	panelPreferredWidth: number;
	courseLeftOpen: boolean;
	/** Настройка пользователя: ширина панели курса. */
	courseLeftPreferredWidth: number;
}

export interface ShellGeometry {
	/**
	 * Ширина правой панели, px.
	 *
	 * Считается ВСЕГДА, в том числе для закрытой панели: закрытие — это
	 * `translate-x-full`, а не схлопывание ширины (`tutor-panel-shell.tsx`), и
	 * обнули мы её здесь — панель не уезжала бы за край, а сжималась в полоску.
	 * Гасит ширину при закрытии тот, кто отжимает контент (`main-layout`), а не
	 * сама панель. На мобильной композиции — 0: там шторка.
	 */
	panelWidth: number;
	/** Фактическая ширина панели курса, px (0 — свёрнута либо не воркспейс). */
	courseLeftWidth: number;
}

/**
 * Ширины колонок для текущего кадра.
 *
 * Чистая функция — тестируется без DOM и без сторов.
 */
export function resolveShellGeometry(input: ShellGeometryInput): ShellGeometry {
	// Мобильная композиция: панели — шторки поверх экрана, отжимать нечего.
	if (!input.isDesktop) return { panelWidth: 0, courseLeftWidth: 0 };

	const panel: ShrinkTrack = { pref: input.panelPreferredWidth, min: SIDE_PANEL_MIN_WIDTH };
	// Место закрытой панели забирают соседи — но саму её ширину считаем так, как
	// будто она открыта (см. `panelWidth` выше).
	const panelIfOpen = input.panelOpen ? panel : ABSENT;
	const available = input.viewportWidth - COLLAPSED_RAIL_WIDTH;

	if (!input.isWorkspace) {
		// Обычная страница: торгуются контент и панель. Рельс считаем свёрнутым —
		// оболочка сама сворачивает его под открытую панель, и завязываться на его
		// текущее состояние значило бы гонять геометрию по кругу.
		const content: ShrinkTrack = {
			pref: REGULAR_CONTENT_COMFORT_WIDTH,
			min: REGULAR_CONTENT_MIN_WIDTH,
		};
		const widths = shrinkToFit([content, panel], available);
		return { panelWidth: widths[1] ?? input.panelPreferredWidth, courseLeftWidth: 0 };
	}

	// Воркспейс: рельс (фикс) + панель курса + сцена + панель репетитора.
	// `pref` сцены — её КОМФОРТНАЯ ширина: пока окно позволяет, лишнее забирает
	// сцена (она `flex-1`), и возвращённое для неё число никого не интересует.
	const left: ShrinkTrack = input.courseLeftOpen
		? { pref: input.courseLeftPreferredWidth, min: COURSE_LEFT_MIN_WIDTH }
		: ABSENT;
	const center: ShrinkTrack = {
		pref: COURSE_CENTER_MIN_WIDTH,
		min: COURSE_CENTER_COMPRESSED_MIN_WIDTH,
	};

	const panelWidth = shrinkToFit([left, center, panel], available)[2] ?? input.panelPreferredWidth;
	const courseLeftWidth = shrinkToFit([left, center, panelIfOpen], available)[0] ?? left.pref;
	return { panelWidth, courseLeftWidth };
}

/**
 * Ширины колонок оболочки для текущего кадра.
 *
 * Хук намеренно самодостаточен (сам читает окно, маршрут и сторы): панель
 * репетитора живёт и на overlay-роутах, где `MainLayout` не смонтирован, и
 * получить геометрию пропсами ей неоткуда. Все потребители обязаны брать ширину
 * отсюда — иначе push контента и сама панель разъедутся на кадре ресайза.
 */
export function useShellGeometry(): ShellGeometry {
	const viewportWidth = useViewportWidth();
	// Desktop-режим спрашиваем у matchMedia, а не у измеренной ширины: монтирование
	// самих колонок ведёт CSS (`lg:flex` в каркасе и панелях), и решать это вторым,
	// чуть иначе округлённым способом — значит на дробной ширине (браузерный зум)
	// получить нулевую ширину у панели, которую CSS всё ещё показывает. Измеренная
	// ширина остаётся ровно для арифметики.
	const isDesktop = useIsLgUp();
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const panelOpen = useSidePanelStore((s) => s.open && s.kind !== null);
	const panelPreferredWidth = useSidePanelWidthStore((s) => s.width);
	const courseLeftOpen = useCourseWorkspaceStore((s) => s.leftOpen);
	const courseLeftPreferredWidth = useCourseWorkspaceStore((s) => s.leftWidth);

	const isWorkspace = isWorkspaceRoute(pathname);
	return useMemo(
		() =>
			resolveShellGeometry({
				viewportWidth,
				isDesktop,
				isWorkspace,
				panelOpen,
				panelPreferredWidth,
				courseLeftOpen,
				courseLeftPreferredWidth,
			}),
		[
			viewportWidth,
			isDesktop,
			isWorkspace,
			panelOpen,
			panelPreferredWidth,
			courseLeftOpen,
			courseLeftPreferredWidth,
		],
	);
}
